import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ShoppingBag, Globe, UtensilsCrossed, Pill, Package } from 'lucide-react';
import { Badge } from './ui/badge';

// Sous-composant pour le diaporama d'images
const ImageSlider = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 6000); // Changé à 6000ms pour correspondre à votre souhait de 6 secondes
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <div className="relative w-full h-full min-h-[300px] lg:min-h-full">
      {images.map((img, index) => (
        <img
          key={index}
          src={img}
          alt={`Slide ${index}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
    </div>
  );
};

export const AppFeatures = () => {
  const features = [
    {
      id: 'local',
      label: 'Achat Local',
      icon: ShoppingBag,
      title: 'Commerce Local Simplifié',
      description: 'Accédez à tous les magasins et produits disponibles au Bénin directement depuis votre mobile.',
      benefits: [
        'Accès direct aux commerces béninois',
        'Paiement sécurisé et rapide',
        'Support aux producteurs locaux',
        'Produits authentiques garantis',
        'Expérience d\'achat unifiée',
      ],
      image: ['/achat-local.jpeg',
      ],
    },
    {
      id: 'international',
      label: 'International',
      icon: Globe,
      title: 'Connexion Internationale',
      description: 'Commandez sur les grandes plateformes mondiales avec une logistique fiable et intégrée.',
      benefits: [
        'Amazon, Cdiscount, Shein, Alibaba...',
        'Processus logistique fiable',
        'Paiement sécurisé',
        'Livraison internationale trackée',
        'Service client dédié',
      ],
      images: ['https://i.postimg.cc/mg0CsPkH/5.png',
              'https://i.postimg.cc/SKv5q785/4.png',
      ],
    },
    {
      id: 'food',
      label: 'Livraison Repas',
      icon: UtensilsCrossed,
      title: 'Livraison de Repas',
      description: 'Soutenez le secteur de la restauration avec une traçabilité complète de la cuisine au client.',
      benefits: [
        'Traçabilité complète des commandes',
        'Code QR pour sécurité maximale',
        'Preuve de livraison',
        'Large choix de restaurants',
        'Temps de livraison optimisé',
      ],
      images: [
        'https://i.postimg.cc/FFM6t7Rd/Whats-App-Image-2026-01-13-at-22-40-45-(5).jpg',
        'https://i.postimg.cc/hjw5tG1Z/45.png',
        'https://i.postimg.cc/Z5hXNCzn/48.png',
      ],
    },
    {
      id: 'pharmacy',
      label: 'Pharmacie Drive',
      icon: Pill,
      title: 'Pharmacie Drive',
      description: 'Innovation dans l\'accès aux médicaments avec digitalisation et optimisation de la chaîne pharmaceutique.',
      benefits: [
        'Ordonnance numérisée sécurisée',
        'Distribution intelligente',
        'Confidentialité médicale garantie',
        'Livraison à domicile ou travail',
        'Service d\'urgence disponible',
      ],
      // Correction : Changé 'image' en 'images' (au pluriel) pour activer le slider
      images: [
        'https://i.postimg.cc/1zXBVQXt/Sans-titre-1.png',
        'https://i.postimg.cc/HL1hZswS/Whats-App-Image-2026-02-03-at-21-34-13.jpg',
        'https://i.postimg.cc/yYwQxYrw/1.jpg',
      ],
    },
    {
      id: 'drive',
      label: 'Syne Drive',
      icon: Package,
      title: 'Casiers Sécurisés',
      description: 'Réseau de casiers physiques pour retrait sécurisé et flexible de vos colis 24/7.',
      benefits: [
        'Retrait 24/7 à votre convenance',
        'Sécurité maximale des colis',
        'Réseau de points relais',
        'Local et international',
        'Notifications en temps réel',
      ],
      images: [
        'https://i.postimg.cc/7YrKsds1/Whats-App-Image-2026-01-14-at-00-16-03.jpg',
        'https://i.postimg.cc/tgFT51Y6/Whats-App-Image-2026-01-14-at-00-17-55.jpg',
      ],
    },
  ];

  return (
    <section id="app" className="py-20 bg-background">
      <div className="container">
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-semibold text-primary mb-3 uppercase tracking-wider">Application SYNE</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Une App <span className="text-primary">Tout-en-Un</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            SYNE place le marché mondial à portée de main avec une interface intuitive et puissante
          </p>
        </div>

        <Tabs defaultValue="local" className="w-full">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 h-auto gap-2 bg-muted/50 p-2 rounded-xl mb-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <TabsTrigger
                  key={feature.id}
                  value={feature.id}
                  className="flex flex-col sm:flex-row items-center gap-2 py-3 px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg transition-all"
                >
                  <Icon size={20} />
                  <span className="text-xs sm:text-sm font-medium">{feature.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <TabsContent key={feature.id} value={feature.id} className="mt-0">
                <Card className="border-border overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid lg:grid-cols-2 gap-0">
                      {/* Section Image ou Diaporama */}
                      <div className="relative h-64 lg:h-auto order-2 lg:order-1 overflow-hidden">
                        {/* Correction : Détection automatique basée sur la présence de 'images' */}
                        {feature.images ? (
                          <ImageSlider images={feature.images} />
                        ) : (
                          <img
                            src={feature.image}
                            alt={feature.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-r from-secondary/80 to-transparent pointer-events-none"></div>
                      </div>

                      {/* Content */}
                      <div className="p-8 lg:p-12 order-1 lg:order-2">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center">
                            <Icon className="text-primary" size={24} />
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            Fonctionnalité SYNE
                          </Badge>
                        </div>

                        <h3 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-4">
                          {feature.title}
                        </h3>
                        <p className="text-muted-foreground mb-6 leading-relaxed">
                          {feature.description}
                        </p>

                        <div className="space-y-3">
                          <h4 className="font-semibold text-foreground text-sm uppercase tracking-wider mb-4">
                            Avantages Clés
                          </h4>
                          {feature.benefits.map((benefit, index) => (
                            <div key={index} className="flex items-start gap-3 group">
                              <div className="mt-1 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                                <div className="w-2 h-2 rounded-full bg-primary"></div>
                              </div>
                              <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                                {benefit}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </section>
  );
};